import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { SkeletonTableRows } from '../../components/SkeletonLoader';
import Pagination from '../../components/Pagination';

const STATUS_LABEL = {
  DRAFT: '임시저장',
  PUBLISHED: '발행됨',
  ARCHIVED: '보관됨',
};

export default function PostList() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '0');
  const status = searchParams.get('status') || '';
  const categoryId = searchParams.get('categoryId') || '';

  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = { page, size: 20 };
    if (status) params.status = status;
    if (categoryId) params.categoryId = categoryId;

    api.get('/api/admin/posts', { params })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/api/admin/categories').then((res) => setCategories(res.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [page, status, categoryId]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) { next.set(key, value); } else { next.delete(key); }
    next.delete('page');
    setSearchParams(next);
  };

  const goPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', p);
    setSearchParams(next);
  };

  const handlePublish = async (id) => {
    if (!window.confirm('발행하시겠습니까?')) return;
    try {
      await api.post(`/api/admin/posts/${id}/publish`);
      toast('글이 발행되었습니다.', 'success');
      load();
    } catch (err) {
      toast(err.response?.data?.message || '발행에 실패했습니다.', 'error');
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('보관 처리하시겠습니까?')) return;
    try {
      await api.post(`/api/admin/posts/${id}/archive`);
      toast('글이 보관되었습니다.', 'info');
      load();
    } catch (err) {
      toast(err.response?.data?.message || '보관 처리에 실패했습니다.', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까? 되돌릴 수 없습니다.')) return;
    try {
      await api.delete(`/api/admin/posts/${id}`);
      toast('글이 삭제되었습니다.', 'info');
      load();
    } catch (err) {
      toast(err.response?.data?.message || '삭제에 실패했습니다.', 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR');
  };

  const posts = data?.content || [];

  return (
    <div className="admin-post-list">
      <div className="list-header">
        <h2>글 목록</h2>
      </div>

      {/* 필터 */}
      <div className="post-filters">
        <div className="filter-tabs">
          {['', 'DRAFT', 'PUBLISHED', 'ARCHIVED'].map((s) => (
            <button
              key={s}
              className={status === s ? 'tab active' : 'tab'}
              onClick={() => setFilter('status', s)}
            >
              {s ? STATUS_LABEL[s] : '전체'}
            </button>
          ))}
        </div>
        <div className="filter-select">
          <select value={categoryId} onChange={(e) => setFilter('categoryId', e.target.value)}>
            <option value="">모든 카테고리</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <table className="post-table">
          <thead>
            <tr>
              <th>제목</th><th>카테고리</th><th>상태</th><th>작성일</th><th>관리</th>
            </tr>
          </thead>
          <tbody>
            <SkeletonTableRows rows={8} />
          </tbody>
        </table>
      ) : (
        <>
          {posts.length === 0 ? (
            <p className="empty-msg">글이 없습니다.</p>
          ) : (
            <table className="post-table">
              <thead>
                <tr>
                  <th>제목</th>
                  <th>카테고리</th>
                  <th>상태</th>
                  <th>작성일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id}>
                    <td>
                      <Link to={`/admin/posts/${post.id}/edit`}>{post.title}</Link>
                      {post.aiGenerated && (
                        <span className="tag-ai" title="AI가 작성한 글">✦ AI</span>
                      )}
                    </td>
                    <td>{post.category?.name || '-'}</td>
                    <td>
                      <span className={`status-badge status-${post.status.toLowerCase()}`}>
                        {STATUS_LABEL[post.status]}
                      </span>
                    </td>
                    <td>{formatDate(post.createdAt)}</td>
                    <td className="post-actions">
                      <Link to={`/admin/posts/${post.id}/edit`} className="btn-sm">편집</Link>
                      {post.status === 'DRAFT' && (
                        <button onClick={() => handlePublish(post.id)} className="btn-sm btn-publish">
                          발행
                        </button>
                      )}
                      {post.status === 'PUBLISHED' && (
                        <button onClick={() => handleArchive(post.id)} className="btn-sm">
                          보관
                        </button>
                      )}
                      <button onClick={() => handleDelete(post.id)} className="btn-sm btn-danger">
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Pagination
            page={page}
            totalPages={data?.totalPages || 0}
            onPageChange={goPage}
            className="pagination"
          />
        </>
      )}
    </div>
  );
}
